import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRef, useEffect, useState } from "react";

export interface CreditBalance {
  monthlyCreditsRemaining: number;
  purchasedCreditsBalance: number;
  subscriptionTier: "free" | "premium";
  creditsResetAt: string | null;
  totalCredits: number;
}

export interface CreditTransaction {
  id: string;
  amount: number;
  type: "MONTHLY_GRANT" | "TOPUP_PURCHASE" | "STORY_GENERATION" | "ROLLBACK" | "SUBSCRIPTION_GRANT";
  storyId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const useCredits = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const previousBalanceRef = useRef<number | null>(null);
  const [hasRecentlyChanged, setHasRecentlyChanged] = useState(false);

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useQuery({
    queryKey: ["credits", user?.id],
    queryFn: async (): Promise<CreditBalance> => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("profiles")
        .select("monthly_credits_remaining, purchased_credits_balance, subscription_tier, credits_reset_at")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Profile not found");

      return {
        monthlyCreditsRemaining: data.monthly_credits_remaining,
        purchasedCreditsBalance: data.purchased_credits_balance,
        subscriptionTier: data.subscription_tier as "free" | "premium",
        creditsResetAt: data.credits_reset_at,
        totalCredits: data.monthly_credits_remaining + data.purchased_credits_balance,
      };
    },
    enabled: !!user,
    staleTime: 30000, // 30 seconds
  });

  // Detect balance changes and trigger pulse animation
  useEffect(() => {
    if (balance?.totalCredits !== undefined) {
      const currentTotal = balance.totalCredits;
      
      if (previousBalanceRef.current !== null && previousBalanceRef.current !== currentTotal) {
        setHasRecentlyChanged(true);
        const timer = setTimeout(() => setHasRecentlyChanged(false), 3000);
        return () => clearTimeout(timer);
      }
      
      previousBalanceRef.current = currentTotal;
    }
  }, [balance?.totalCredits]);

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["credit-transactions", user?.id],
    queryFn: async (): Promise<CreditTransaction[]> => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("credit_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      return (data || []).map((t) => ({
        id: t.id,
        amount: t.amount,
        type: t.type as CreditTransaction["type"],
        storyId: t.story_id,
        metadata: t.metadata as Record<string, unknown>,
        createdAt: t.created_at,
      }));
    },
    enabled: !!user,
  });

  const deductCredit = useMutation({
    mutationFn: async (storyId: string) => {
      const { data, error } = await supabase.rpc("deduct_credit", {
        p_user_id: user?.id,
        p_story_id: storyId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; source?: string };
      if (!result.success) {
        throw new Error(result.error || "Failed to deduct credit");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["credits", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["credit-transactions", user?.id] });
    },
  });

  const hasCredits = (balance?.totalCredits ?? 0) > 0;
  const isPremium = balance?.subscriptionTier === "premium";

  return {
    balance,
    transactions,
    balanceLoading,
    transactionsLoading,
    hasCredits,
    isPremium,
    hasRecentlyChanged,
    deductCredit,
    refetchBalance,
  };
};
